import { Client, UserRole } from '@cc/schema';
import { useGetIdentity, useList } from '@refinedev/core';
import { Select } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { IUser } from '../providers';

/**
 * P011 · THE practice client roster — read ONCE, WHOLE, and shared by every screen that has to
 * NAME, FILTER or GATE a row by its client.
 *
 * The shipped read asked for `subWarnings.clientsPurchased * 4` rows, and `clientsPurchased` falls
 * back to `FREE_SUBSCRIPTION_APPS` (5) whenever the practice has no Stripe quantity — a 20-row
 * page. Every practice with more clients than that got a TRUNCATED roster, and the pages that read
 * it drew the wrong conclusions about every row below the cut: the client column said "unknown"
 * (the raw `users/…` id), the client filter offered only the first page of names, and the edit gate
 * — which reads `access.allowEdit` OFF THE ROSTER ROW — found no row at all and so offered no
 * pencil. The founder saw it as rows for one client having the pencil and rows for the next three
 * not having it, on identical permissions (E02 QA 2026-09-02, defect A).
 *
 * Measured at the wire on the demo practice (2026-09-02): `clients` reports `count: 27` — rows, one
 * per relation edge — for 25 distinct clients, and the shipped `limit: 20` page carried only 18 of
 * them. Seven clients were unnameable, unfilterable and ungateable on every tool list page.
 *
 * So the roster is read whole. `clients` reports `count` (an unbounded `fullCount` on the same
 * permission-scoped AQL — `resolvers/portal/client.ts`, `considerPermissions` untouched) and its
 * `limit` has no server-side cap, so one page above any practice we serve plus ONE re-read when the
 * reported count exceeds it is enough; there is no cursor to walk and no second round trip in the
 * common case.
 *
 * Every consumer shares this one query — same resource, same fields, same page size — so the whole
 * portal issues a single roster request, and no screen can disagree with another about who the
 * practice's clients are.
 */
export const CLIENT_ROSTER_PAGE_SIZE = 200;

/**
 * The UNION of what the screens read off a roster row, so no page loses a field by sharing:
 * `uid` (exposure/edit's storage prefix), `diagnoses` (conceptualization), `role` +
 * `preferredPronouns` (sessions), `accessLevel` + `access` (the edit gates).
 */
const CLIENT_ROSTER_FIELDS = [
  {
    data: [
      '_id',
      'uid',
      'firstName',
      'lastName',
      'email',
      'accessLevel',
      'role',
      'diagnoses',
      'preferredPronouns',
      {
        access: ['allowView', 'allowEdit', 'allowAdminView', 'allowAdminEdit'],
      },
    ],
  },
  'count',
];

type RosterResponse = { data: Client[]; count: number };

/**
 * The rows a roster read returned, as a SET of clients.
 *
 * `getPortalDocuments`' practice branch collects one row per matching `therapistClientRelations`
 * edge, so a client reachable through two edges (own + admin-view) comes back twice — which is why
 * `count` (29 on the demo practice) exceeds the number of clients (27), and why an un-deduped
 * roster offered the same person twice in a client filter. One row per `_id`, first wins.
 */
const rosterOf = (payload: unknown): Client[] => {
  const rows =
    ((payload as { data?: RosterResponse } | undefined)?.data
      ?.data as Client[]) || [];
  const byId = new Map<string, Client>();
  rows.forEach((c) => {
    if (c?._id && !byId.has(c._id)) byId.set(c._id, c);
  });
  return Array.from(byId.values());
};

export const useClientRoster = () => {
  // one page above any practice we serve; grown to the reported count if a bigger one appears
  const [rosterSize, setRosterSize] = useState(CLIENT_ROSTER_PAGE_SIZE);

  const {
    data,
    isError: clientsError,
    isLoading: clientsLoading,
    refetch,
  } = useList<RosterResponse>({
    resource: 'clients',

    meta: {
      fields: CLIENT_ROSTER_FIELDS,
    },

    pagination: {
      current: 1,
      pageSize: rosterSize,
    },
  });

  const clientsCount = data?.total ?? 0;

  useEffect(() => {
    if (clientsCount > rosterSize) setRosterSize(clientsCount);
  }, [clientsCount, rosterSize]);

  const clients = useMemo(() => rosterOf(data), [data]);

  /** Re-read the roster and hand back the clients themselves, not the transport envelope. */
  const refetchClients = useCallback(async () => {
    const res = await refetch();
    return rosterOf(res?.data);
  }, [refetch]);

  return {
    /** every client of this practice this therapist may see — deduped, never truncated */
    clients,
    /** rows the server matched (edges, so ≥ `clients.length`) */
    clientsCount,
    clientsLoading,
    clientsError,
    refetchClients,
  };
};

export const useClientOptions = () => {
  const { data: user } = useGetIdentity<IUser>();

  const { clients, clientsLoading } = useClientRoster();

  const memoizedClientsOptions = useMemo(() => {
    return clients
      .filter((c) => {
        return !!c.accessLevel;
      })
      .filter(
        (c) =>
          c.access?.allowEdit ||
          (user?.db?.role === UserRole.THERAPIST_ADMIN &&
            c.access?.allowAdminEdit),
      )
      .map((client) => (
        <Select.Option key={client._id} value={client._id}>
          {client.firstName + ' ' + client.lastName + ' - (' + client._id + ')'}
        </Select.Option>
      ));
  }, [user?.db?.role, clients]);

  return {
    clientsLoading,
    memoizedClientsOptions,
    briefClientInfo: clients,
  };
};
